import { NavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useFeatureFlags, type FeatureKey } from "../context/FeatureFlagsContext";
import { LegalLinks } from "../pages/legal/LegalLinks";
import "./Sidebar.css";

interface NavItem {
  path: string;
  label: string;
  icon: string;
  /** Tester feature flag; omitted for MVP items that are always shown. */
  feature?: FeatureKey;
}

const navItems: NavItem[] = [
  { path: "/", label: "Dashboard", icon: "📊", feature: "dashboard" },
  { path: "/upload", label: "Upload", icon: "📤" },
  { path: "/documents", label: "Documents", icon: "📁" },
  { path: "/activity", label: "Activity", icon: "📋", feature: "activity_log" },
  { path: "/team", label: "Team", icon: "👥", feature: "team" },
  { path: "/billing", label: "Billing", icon: "💳" },
];

interface SidebarProps {
  mobileMenuOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ mobileMenuOpen = false, onClose }: SidebarProps) {
  const { isPlatformAdmin } = useAuth();
  const { flags } = useFeatureFlags();
  const visibleItems = navItems.filter((item) => !item.feature || flags[item.feature]);

  const handleLinkClick = () => {
    // Close mobile menu when a link is clicked
    if (onClose) {
      onClose();
    }
  };

  return (
    <aside className={`sidebar ${mobileMenuOpen ? "sidebar--mobile-open" : ""}`}>
      <div className="sidebar-brand">
        <span className="sidebar-logo">H</span>
        <span className="sidebar-title">Haven</span>
        <button 
          className="sidebar-close" 
          onClick={onClose}
          aria-label="Close menu"
        >
          ✕
        </button>
      </div>
      <nav className="sidebar-nav">
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? "sidebar-link--active" : ""}`
            }
            end={item.path === "/"}
            onClick={handleLinkClick}
          >
            <span className="sidebar-link-icon">{item.icon}</span>
            <span className="sidebar-link-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-footer">
        {isPlatformAdmin && (
          <a href="/admin/" className="sidebar-link" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">🛡️</span>
            <span className="sidebar-link-label">Admin Console</span>
          </a>
        )}
        {flags.settings_page && (
          <NavLink to="/settings" className="sidebar-link" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">⚙️</span>
            <span className="sidebar-link-label">Settings</span>
          </NavLink>
        )}
        <LegalLinks className="sidebar-legal" onClick={handleLinkClick} />
      </div>
    </aside>
  );
}
